import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type SlideChangedEvent } from '@/scripts/types/events'
import { UcoastEl } from '@/scripts/core/UcoastEl'

export function isSliderComponent(obj: HTMLElement): obj is SliderComponent {
	if (!obj) return false
	if (obj.localName !== 'slider-component') return false
	return true
}

export class SliderComponent extends UcoastEl {
	static htmlSelector = 'slider-component'
	slider: HTMLElement
	sliderItems?: NodeListOf<HTMLElement>
	sliderItemsToShow?: HTMLElement[]
	sliderItemOffset?: number
	slidesPerPage?: number
	totalPages?: number
	currentPage?: number = 1
	slideScrollPosition?: number
	enableSliderLooping: boolean
	currentPageElement?: HTMLElement
	pageTotalElement?: HTMLElement
	prevButton?: HTMLButtonElement
	nextButton?: HTMLButtonElement
	deltaX: number
	lastHref?: string
	slideWidth?: number
	isDraggable: boolean
	isMobileDraggable: boolean
	mouseState: 'up' | 'down' | 'moved'

	constructor() {
		super()
		this.deltaX = 0
		this.mouseState = 'up'
		this.isDraggable = this.hasAttribute('data-uc-draggable')
		this.isMobileDraggable = this.hasAttribute('data-uc-mobile-draggable')
		this.slider = q.rs('[id^="Slider-"]', this)
		this.sliderItems = q.ol('[id^="Slide-"]', this)
		this.enableSliderLooping = false
		this.currentPageElement = q.os('.slider-counter--current', this)
		this.pageTotalElement = q.os('.slider-counter--total', this)
		this.prevButton = q.os('button[name="previous"]', this)
		this.nextButton = q.os('button[name="next"]', this)

		this.initPages()
		const resizeObserver = new ResizeObserver(
			(_entries: ResizeObserverEntry[]) => this.initPages()
		)
		resizeObserver.observe(this.slider)

		this.slider.addEventListener('scroll', this.update.bind(this))
		this.prevButton?.addEventListener(
			'click',
			this.onButtonClick.bind(this)
		)
		this.nextButton?.addEventListener(
			'click',
			this.onButtonClick.bind(this)
		)

		if (this.isDraggable) {
			this.initDraggableSlider()
		}
		if (this.isMobileDraggable) {
			this.initMobileDraggableSlider()
		}
		if (this.isDraggable || this.isMobileDraggable) {
			this.sliderItems?.forEach((item) => {
				if (!this.slideWidth) {
					this.slideWidth = item.offsetWidth
				}
				const innerAnchors = q.ol('a', item)
				if (innerAnchors) {
					for (let i = 0; i < innerAnchors.length; i++) {
						const anchor = innerAnchors[i]
						anchor.draggable = false
						anchor.addEventListener('dragstart', (e) =>
							e.preventDefault()
						)
						anchor.addEventListener('click', (e) => {
							e.preventDefault()
						})
						anchor.addEventListener('mousedown', (e) => {
							this.lastHref = q.oa(
								q.rct(e),
                'href'
							)
						})
					}
				}
			})
		}
	}

	initPages() {
		this.sliderItemsToShow = this.sliderItems
			? Array.from(this.sliderItems).filter(
					(element) => element.clientWidth > 0
				)
			: []
		if (this.sliderItemsToShow.length < 2) return
		this.sliderItemOffset =
			this.sliderItemsToShow[1].offsetLeft -
			this.sliderItemsToShow[0].offsetLeft
		this.slider.setAttribute(
			'data-offset',
			this.sliderItemOffset.toString()
		)
		this.slidesPerPage = Math.floor(
			(this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
				this.sliderItemOffset
		)
		this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1
		this.update()
	}

	resetPages() {
		this.sliderItems = this.querySelectorAll('[id^="Slide-"]')
		this.initPages()
	}

	createSlideChangedEvent(): SlideChangedEvent {
		const currentPage = this.currentPage
		if (!currentPage)
			throw new Error(
				'cannot createSlideChangedEvent - currentPage is undefined'
			)
		if (!this.sliderItemsToShow)
			throw new Error(
				'cannot createSlideChangedEvent - sliderItemsToShow is undefined'
			)
		const currentElement = this.sliderItemsToShow[currentPage - 1]
		if (!currentElement)
			throw new Error(
				'cannot createSlideChangedEvent - currentElement is undefined'
			)
		return new CustomEvent('slideChanged', {
			detail: {
				currentPage,
				currentElement,
			},
		})
	}

	update() {
		// Temporarily prevents unneeded updates resulting from variant changes
		// This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
		if (!this.slider || !this.nextButton) return

		const previousPage = this.currentPage
		if (!this.sliderItemOffset || this.sliderItemOffset === 0) {
			this.currentPage = 1
		} else {
			this.currentPage =
				Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1
		}

		if (this.currentPageElement && this.pageTotalElement) {
			this.currentPageElement.textContent = `${this.currentPage}`
			this.pageTotalElement.textContent = `${this.totalPages}`
		}

		if (this.currentPage != previousPage) {
			if (this.sliderItemsToShow) {
				this.dispatchEvent(this.createSlideChangedEvent())
			} else {
				throw new Error('sliderItemsToShow is undefined')
			}
		}

		if (this.enableSliderLooping) return
	}

	isSlideVisible(element: HTMLElement, offset = 0) {
		const lastVisibleSlide =
			this.slider.clientWidth + this.slider.scrollLeft - offset
		return (
			element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
			element.offsetLeft >= this.slider.scrollLeft
		)
	}

	onButtonClick(event: MouseEvent) {
		event.preventDefault()
		const currentTarget = q.rct(event)
		if (!(currentTarget instanceof HTMLButtonElement)) return
		if (!this.sliderItemOffset) return
		const step = currentTarget.dataset.step
			? parseInt(currentTarget.dataset.step)
			: 1

		this.slideScrollPosition =
			currentTarget.name === 'next'
				? this.slider.scrollLeft + step * this.sliderItemOffset
				: this.slider.scrollLeft - step * this.sliderItemOffset

		const dynamic = q.oa(
			currentTarget,
      'data-uc-dynamic-button'
		)

		if (
			dynamic === 'yes' &&
			this.prevButton &&
			this.nextButton &&
			this.pageTotalElement &&
			this.currentPageElement
		) {
			const hide = (button: HTMLButtonElement) => {
				button.classList.add('hide')
				button.classList.remove('show')
			}
			const show = (button: HTMLButtonElement) => {
				button.classList.add('show')
				button.classList.remove('hide')
			}
			if (currentTarget.name === 'previous') {
				if ((this.currentPage as number) - 1 === 1) {
					hide(this.prevButton)
				}
				show(this.nextButton)
			} else {
				if ((this.currentPage as number) + 1 === this.totalPages) {
					hide(this.nextButton)
				}
				show(this.prevButton)
			}
		}

		this.setSlidePosition(this.slideScrollPosition)
	}

	setSlidePosition(position: number) {
		this.slider.scrollTo({
			left: position,
		})
	}

	initDraggableSlider() {
		this.slider.addEventListener('mousedown', this.onMouseDown.bind(this))
	}

	initMobileDraggableSlider() {
		console.warn('draggable not available for slider-component')
	}

	getLastHref() {
		return this.lastHref
	}

	handleMouseAction(mouseAction: 'up' | 'down', targetElement?: HTMLElement) {
		if (mouseAction === 'down') {
			this.mouseState = 'down'
			return
		}
		if (mouseAction === 'up') {

			if (this.mouseState === 'down' && this.lastHref) {
				this.mouseState = 'up'
				window.location.href = this.lastHref
				return
			}
			this.mouseState = 'up'
			return
		}
	}

	onMouseDown(event: MouseEvent) {
		const clientX = event.clientX
		this.deltaX = 0
		this.handleMouseAction('down', q.oct(event))

		const onMouseMove = (e: MouseEvent) => {
			e.preventDefault()
			const currentClientX = e.clientX
			const deltaX = clientX - currentClientX
			this.deltaX = deltaX
			if (this.mouseState === 'down') {
				this.mouseState = 'moved'
			}
		}

		const onMouseUp = (event: MouseEvent) => {
			event.preventDefault()
			this.handleMouseAction('up')
			document.removeEventListener('mousemove', onMouseMove)
			document.removeEventListener('mouseup', onMouseUp)

			if (this.deltaX < 0) {
				if (!this.slideWidth || !this.currentPage) {
					throw new Error('no slide found, cannot scroll slider')
				}
				const moveDistance =
					this.slider.scrollLeft - this.slideWidth - 5
				this.slider.scrollLeft = moveDistance
			} else if (this.deltaX > 0) {
				if (!this.slideWidth || !this.currentPage) {
					throw new Error('no slide found, cannot scroll slider')
				}
				const moveDistance =
					this.slider.scrollLeft + this.slideWidth + 5
				this.slider.scrollLeft = moveDistance
			}
		}

		document.addEventListener('mousemove', onMouseMove)
		document.addEventListener('mouseup', onMouseUp)
	}
}
